import I18N from '@leverege/i18n';
import { LinkCellRenderer } from '@leverege/molten/imagine';
import Path from '@leverege/path';
import { toUiAttributesType } from "../util/OpenApiTypeUtil.js";
import Util from './Util.js';
export default class OpenApiAttribute {
  constructor(blueprint, model) {
    this.blueprint = blueprint;
    this.model = model;
    this.tags = model['x-lev-tags'] ?? [];
    this.type = toUiAttributesType(model.type, model);
    this.i18n = I18N.ns(`openApi.blueprint.${blueprint.type}.attributes.${this.path}`);
    this.required = blueprint.model.required?.includes(this.path);
    this.sortable = model['x-lev-sortable'];
    this.filterable = model['x-lev-filterable'];
  }
  get path() {
    return this.model.path;
  }
  install(molten) {
    molten.addPlugin('Attribute', this.toPlugin());
    if (this.hasTag('tableCell:link')) {
      molten.addPlugin('AttributeCellRenderer', {
        objectType: this.blueprint.type,
        attrName: this.path,
        renderer: LinkCellRenderer,
        handles: () => true
      });
    }
  }
  getName = () => {
    return this.i18n.t('name', this.model.title ?? this.path, {
      count: 1
    });
  };
  getDescription = () => {
    return this.getString('description', this.model.description);
  };
  getString = (strName, defaultValue) => {
    return this.i18n.t(strName, defaultValue);
  };
  get defaultValue() {
    return this.model.default;
  }
  getEditOptions() {
    const formSort = this.model['x-lev-form-sort'] ?? (this.required ? 'a' : 'b');
    if (this.model.enum) {
      return {
        editor: this.editorType ?? 'enum',
        editorProps: {
          values: Util.getEnumValues(this.model, this)
        },
        formSort,
        hint: this.model['x-lev-hint']
      };
    }
    return {
      editor: this.editorType,
      validator: this.editValidator,
      formSort,
      hint: this.model['x-lev-hint']
    };
  }
  getCreateOptions() {
    const formSort = this.model['x-lev-form-sort'] ?? (this.required ? 'a' : 'b');
    if (this.model.enum) {
      return {
        editor: this.editorType ?? 'enum',
        editorProps: {
          values: Util.getEnumValues(this.model, this)
        },
        formSort,
        hint: this.model['x-lev-hint']
      };
    }
    return {
      editor: this.editorType,
      validator: this.createValidator,
      formSort,
      hint: this.model['x-lev-hint']
    };
  }
  get editorType() {
    return this.model['x-lev-editor-type'];
  }
  get editValidator() {
    if (this.model['x-lev-edit-validator-type']) {
      return this.getValidator(this.model['x-lev-edit-validator-type']);
    }
    return this.getValidator(this.validator);
  }
  get createValidator() {
    if (this.model['x-lev-create-validator-type']) {
      return this.getValidator(this.model['x-lev-create-validator-type']);
    }
    return this.getValidator(this.validator);
  }
  get validator() {
    return this.model['x-lev-validator-type'];
  }
  getValidator(type) {
    if (this.required && type) {
      return {
        type: 'composite',
        validators: ['required', 'type']
      };
    }
    if (!this.required && type) {
      return {
        type
      };
    }
    if (this.required && !type) {
      return {
        type: 'required'
      };
    }
    return null;
  }
  toPlugin() {
    const p = Path(this.path);
    const {
      getName
    } = this;
    return {
      name: this.path,
      path: this.path,
      objectType: this.blueprint.type,
      valueType: this.type,
      get displayName() {
        return getName();
      },
      source: {
        type: this.blueprint.apiName,
        model: this.model,
        editOptions: this.getEditOptions(),
        createOptions: this.getCreateOptions(),
        creatable: this.model['x-lev-creatable'] ?? !this.model.readOnly,
        editable: this.model['x-lev-editable'] ?? !this.model.readOnly,
        sortable: this.sortable,
        filterable: this.filterable
      },
      get: (data, cxt) => {
        return p.get(data, this.defaultValue);
      },
      canSet: () => true,
      set: (data, value, cxt) => {
        return p.copy(data, value);
      },
      getPatch: (data, value, cxt) => {
        return {
          path: this.path,
          value
        };
      },
      getString: this.getString,
      getDescription: this.getDescription,
      getOption: optionName => {
        return this[optionName];
      }
    };
  }
  toNamePlugin() {
    const p = Path(this.path);
    const {
      getName
    } = this;
    return {
      name: 'name',
      path: this.path,
      objectType: this.blueprint.type,
      valueType: this.type,
      get displayName() {
        return getName();
      },
      get: (data, cxt) => {
        return p.get(data, this.defaultValue);
      },
      getString: this.getString,
      getDescription: this.getDescription
    };
  }

  /**
   * Returns true if the tags include the specified tab
   */
  hasTag(tag) {
    return this.tags.includes(tag);
  }

  /**
     * Returns the first tag starting with the prefix
     * If strip is true, this will return the value of the 
     * tag after the prefix
     */
  getTagStartingWith(prefix, strip) {
    const arr = this.tags;
    if (arr == null) {
      return null;
    }
    const val = arr.find(t => t.startsWith(prefix));
    if (val && strip) {
      return val.substring(prefix.length);
    }
    return val;
  }

  /**
     * Returns all tags starting with the prefix
     * If strip is true, this will return the value of the 
     * tag after the prefix
     */
  getTagsStartingWith(prefix, strip) {
    let arr = this.tags;
    if (arr == null) {
      return [];
    }
    arr = arr.filter(t => t.startsWith(prefix));
    if (arr.length > 0 && strip) {
      return arr.map(val => val.substring(prefix.length));
    }
    return arr;
  }
}